/* Copyright 2016-2022
   Kabandy
   Author: Jonathan Boosten
 */

import { IQueryParam, ITime, ITimeSerialized } from ".";
import { Timestamp } from "@google-cloud/firestore";

export module AccessDataModel {
    export enum EErrorMsg {
        Create = "Can't create access",
        Update = "Can't update access"
    }
    export enum ECreateErrorReason {
        AccessAlreadyExists = "Access already exists",
        AccessUserTypeNotFound = "Access of type User not found",
        AccessPeriodAlreadyExists = "Access period already exits",
        AccessProjectTypeNotFound = "Access of type Project not found",
        ContractExpired = "Contract has expired",
        ContractMaxAccessesCountReached = "Maximum of accesses has been reached",
        ContractNotFound = "Contract not found",
        CrossContractAccessConflict = "Cross contract access conflict",
        ProjectIdMissing = "Project id is missing",
        ProjectNotFound = "Project not found",
        RoleIdMissing = "Role id is missing",
        RoleNotFound = "Role not found",
        UserIdMissing = "User id is missing",
        UserNotFound = "User not found"
    }

    export enum EUpdateErrorReason {
        AccessNotFound = "Access not found",
        InvalidField = "Invalid field"
    }

    export enum EAccessParent {
        Contract = "contract",
        Project = "project",
        User = "user"
    }
    export enum EAccessType {
        User = "User",
        Project = "Project",
        UserProject = "UserProject"
    }
    export interface IAccess {
        id: string;
        contractId: string;
        type: typeof EAccessType[keyof typeof EAccessType];
        expired: boolean;
        time: ITime | ITimeSerialized;
        userId?: string;
        projectId?: string;
        roleId?: string;
        isAdmin?: boolean;
        isActive?: boolean; // api purpose
        tokenExpiration?: Timestamp; // api purpose
    }

    export interface IAccessSerialized extends IAccess {
        time: ITimeSerialized;
    }

    export interface IAccessUserType extends IAccess {
        type: EAccessType.User;
        userId: string;
    }
    export interface IAccessProjectType extends IAccess {
        type: EAccessType.Project;
        projectId: string;
    }
    export interface IAccessUserProjectType extends IAccess {
        type: EAccessType.UserProject;
        userId: string;
        projectId: string;
    }

    export interface IAccessCreateInfo {
        type: typeof EAccessType[keyof typeof EAccessType];
        parent: typeof EAccessParent[keyof typeof EAccessParent];
        contractId?: string;
        accessId?: string;
        userId?: string;
        projectId?: string;
        roleId?: string;
        isAdmin?: boolean;
        expired?: boolean;
        isActive?: boolean; // api purpose
        tokenExpirationUnixTimestamp?: number; // api purpose
    }
    export interface IAccessDeleteInfo {
        parent: typeof EAccessParent[keyof typeof EAccessParent];
        accessId: string;
        type?: typeof EAccessType[keyof typeof EAccessType];
        contractId?: string;
        userId?: string;
        projectId?: string;
    }
    export interface IAccessGetInfo {
        parent: typeof EAccessParent[keyof typeof EAccessParent];
        contractId?: string;
        userId?: string;
        projectId?: string;
        accessId: string;
    }
    export interface IAccessQueryInfo {
        parent: typeof EAccessParent[keyof typeof EAccessParent];
        contractId?: string;
        userId?: string;
        projectId?: string;
        filterOptions?: IAccessQueryInfoFilterOptions;
    }
    export interface IAccessQueryInfoFilterOptions {
        type?: typeof EAccessType[keyof typeof EAccessType];
        contractId?: string;
        userId?: string;
        projectId?: string;
        roleId?: string;
        isAdmin?: boolean;
        expired?: boolean;
    }

    export interface IAccessUpdateInfo {
        parent: typeof EAccessParent[keyof typeof EAccessParent];
        accessId: string;
        contractId?: string;
        userId?: string;
        projectId?: string;
        roleId?: string;
        isAdmin?: boolean;
        expired?: boolean;
        isActive?: boolean; // api purpose
        tokenExpirationUnixTimestamp?: number; // api purpose
    }

    //
    // --- contract/:contractId/access/:accessId Create|Delete|Get|Query|Update info

    export interface IAccessContractCreateInfo extends IAccessCreateInfo {
        parent: EAccessParent.Contract;
        contractId: string;
    }

    export interface IAccessUserTypeCreateInfo extends IAccessContractCreateInfo {
        type: EAccessType.User;
        userId: string;
    }
    export interface IAccessProjectTypeCreateInfo extends IAccessContractCreateInfo {
        type: EAccessType.Project;
        projectId: string;
    }
    export interface IAccessUserProjectTypeCreateInfo extends IAccessContractCreateInfo {
        type: EAccessType.UserProject;
        userId: string;
        projectId: string;
    }

    export interface IAccessContractDeleteInfo extends IAccessDeleteInfo {
        contractId: string;
    }
    export interface IAccessUserTypeDeleteInfo extends IAccessContractDeleteInfo {
        type: EAccessType.User;
    }
    export interface IAccessProjectTypeDeleteInfo extends IAccessContractDeleteInfo {
        type: EAccessType.Project;
    }
    export interface IAccessUserProjectTypeDeleteInfo extends IAccessContractDeleteInfo {
        type: EAccessType.UserProject;
    }
    export interface IAccessContractGetInfo extends IAccessGetInfo {
        contractId: string;
    }
    export interface IAccessContractQueryInfo extends IAccessQueryInfo {
        contractId: string;
    }
    export interface IAccessContractUpdateInfo extends IAccessUpdateInfo {
        parent: EAccessParent.Contract;
        contractId: string;
    }

    //
    // --- user/:userId/access/:accessId Create|Delete|Get|Query|Update info

    export interface IAccessUserCreateInfo extends IAccessCreateInfo {
        parent: EAccessParent.User;
        userId: string;
    }
    export interface IAccessUserDeleteInfo extends IAccessDeleteInfo {
        userId: string;
    }
    export interface IAccessUserGetInfo extends IAccessGetInfo {
        userId: string;
    }
    export interface IAccessUserQueryInfo extends IAccessQueryInfo {
        userId: string;
    }
    export interface IAccessUserUpdateInfo extends IAccessUpdateInfo {
        parent: EAccessParent.User;
        userId: string;
    }

    //
    // --- project/:projectId/access/:accessId Delete|Get|Query|Update info

    export interface IAccessProjectCreateInfo extends IAccessCreateInfo {
        parent: EAccessParent.Project;
        projectId: string;
    }
    export interface IAccessProjectDeleteInfo extends IAccessDeleteInfo {
        projectId: string;
    }
    export interface IAccessProjectGetInfo extends IAccessGetInfo {
        projectId: string;
    }
    export interface IAccessProjectQueryInfo extends IAccessQueryInfo {
        projectId: string;
    }
    export interface IAccessProjectUpdateInfo extends IAccessUpdateInfo {
        parent: EAccessParent.Project;
        projectId: string;
    }

    export module Database {
        export interface ICreateInfo extends IAccessCreateInfo {
            parentCollection: string;
            parentId: string;
        }
        export interface IDeleteInfo {
            parentCollection: string;
            parentId: string;
            accessId: string;
        }
        export interface IGetInfo {
            parentCollection: string;
            parentId: string;
            accessId: string;
        }
        export interface IQueryInfo {
            parentCollection: string;
            parentId: string;
            queryParam: IQueryParam;
        }
        export interface IUpdateInfo extends IAccessUpdateInfo {
            parentCollection: string;
            parentId: string;
        }
    }
}
